import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import api, { handleAxiosError } from '../../../utils/api';

interface TaskUploadState {
  loading: boolean;
  deleting: boolean;
  bulkDeleteStatus: string | undefined;
  error: string | null;
  message: string | null;
  qualityAcceptedTasks: string[];
  wrongTaskIds: string[];
  deletedTaskIds: string[];
}

const initialState: TaskUploadState = {
  loading: false,          
  deleting: false,    
  bulkDeleteStatus: 'NOT_STARTED',     
  error: null,
  message: null,
  qualityAcceptedTasks: [],
  wrongTaskIds: [],
  deletedTaskIds: [],
};

export const checkQualityAcceptedTasks = createAsyncThunk<
  { qualityAcceptedTasks: string[]; message: string },
  { projectId: string; file: File },
  { rejectValue: { message: string; wrongTaskIds?: string[] } }
>('upload/checkQualityAcceptedTasks', async ({ projectId, file }, thunkAPI) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const res = await api.post(
      `/task/company/quality_accepted_tasks/?project_id=${projectId}`,
      formData    
    );

    const { data, message } = res.data;
    return {
      qualityAcceptedTasks: data.quality_accepted_task_ids || [],
      message: message || 'Quality-accepted tasks present',
    };
  } catch (err: any) {
    if (err.response?.status === 400) {
      return thunkAPI.rejectWithValue({
        message: err.response.data.message || err.response.data.error || " Unknown Error",
        wrongTaskIds: err.response.data.data?.wrong_task_ids,
      });
    }
    handleAxiosError(err);
    return thunkAPI.rejectWithValue({ message: err.message });
  }
});


export const bulkDeleteTasks = createAsyncThunk<
  { deletedTaskIds: string[]; message: string },
  { projectId: string; file: File },
  { rejectValue: { message: string; wrongTaskIds?: string[] } }
>('upload/bulkDeleteTasks', async ({ projectId, file }, thunkAPI) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const res = await api.post(
      `/task/company/bulk_delete_tasks/?project_id=${projectId}`,
      formData
    );

    const { data, message } = res.data;
    return {
      deletedTaskIds: data.deleted_task_ids,
      message: message || 'Tasks deleted',
    };
  } catch (err: any) {
    if (err.response?.status === 400) {
      return thunkAPI.rejectWithValue({
        message: err.response.data.message,
        wrongTaskIds: err.response.data.data?.wrong_task_ids,
      });
    }
    handleAxiosError(err);
    return thunkAPI.rejectWithValue({ message: err.message });
  }
});


const taskUploadSlice = createSlice({
  name: 'uploadTask',
  initialState,
  reducers: { clearTaskUploadState: () => initialState,
    setBulkDeleteStatus: (state, action: PayloadAction<string>) => {
      state.bulkDeleteStatus = action.payload;
    }
   },
  extraReducers: (b) => {
    
    b.addCase(checkQualityAcceptedTasks.pending, (s) => {
      s.loading = true; s.error = null; s.wrongTaskIds = [];
    })
     .addCase(checkQualityAcceptedTasks.fulfilled, (s, a) => {
       s.loading = false;
       s.qualityAcceptedTasks = a.payload.qualityAcceptedTasks;
       s.message = a.payload.message;
     })
     .addCase(checkQualityAcceptedTasks.rejected, (s, a) => {
       s.loading = false;
       s.error = a.payload?.message || a.error.message!;
       s.wrongTaskIds = a.payload?.wrongTaskIds || [];
     });

   
    b.addCase(bulkDeleteTasks.pending, (s) => {
      s.deleting = true; s.error = null; s.wrongTaskIds = [];
      s.bulkDeleteStatus = 'PENDING'
    })
     .addCase(bulkDeleteTasks.fulfilled, (s, a) => {
       s.deleting = false;
       s.deletedTaskIds = a.payload.deletedTaskIds;
       s.message = a.payload.message;
       s.bulkDeleteStatus = 'SUCESS';
     })
     .addCase(bulkDeleteTasks.rejected, (s, a) => {
       s.deleting = false;
       s.error = a.payload?.message || a.error.message!;
       s.bulkDeleteStatus = 'FAILED';
       s.message = a.payload?.message || a.error.message!;
       s.wrongTaskIds = a.payload?.wrongTaskIds || [];
     });
  },
});

export const { clearTaskUploadState, setBulkDeleteStatus } = taskUploadSlice.actions;
export default taskUploadSlice.reducer;
